import React, { useEffect, useState, useMemo } from 'react';
import { ArrowRight, ArrowLeft, BookOpen } from 'lucide-react';
import { PROFITABILITY_VARIABLES, AFFORDABILITY_VARIABLES } from '../constants';
import VariableRow from '../components/VariableRow';
import ScoreCard from '../components/ScoreCard';
import AddressAutocomplete from '../../../../common/AddressAutocomplete';
import ProgramChips from '../components/ProgramChips';
import BuildingsEditor from '../components/BuildingsEditor';
import BaselineSummary from '../components/BaselineSummary';
import BaselineFetchProgress from '../components/BaselineFetchProgress';
import DocumentGuidanceModal from '../components/DocumentGuidanceModal';

// Smart merge across baseline refreshes:
//   - Variable not in prev          → use proxy default outright
//   - Variable in prev as 'proxy'   → REPLACE with fresh proxy (the whole
//                                     point of refreshing baseline is to
//                                     pull updated market data — old proxy
//                                     ratings should yield to new ones)
//   - Variable in prev as 'doc_verified' or 'self_selected' → KEEP user's
//     rating + provenance, but merge proxy raw fields IN to fill gaps (e.g.
//     a doc-verified rent_to_ami_burden missing AMI gets the fresh proxy AMI)
function mergeProxyIntoState(defaults, prev) {
    const merged = { ...prev };
    Object.entries(defaults).forEach(([key, defaultValue]) => {
        const prevValue = prev[key];
        if (!prevValue) {
            merged[key] = defaultValue;
            return;
        }
        if (prevValue.provenance === 'proxy') {
            // Old data was proxy-sourced — replace with fresh proxy outright.
            merged[key] = defaultValue;
            return;
        }
        // Doc-verified or self-selected — preserve user's rating + provenance,
        // merge proxy raw underneath to fill missing fields.
        const prevRaw = Object.fromEntries(
            Object.entries(prevValue.raw || {}).filter(([, v]) => v != null && v !== '')
        );
        const mergedRaw = {
            ...(defaultValue.raw || {}),
            ...prevRaw,  // user/doc data wins per-field
        };
        merged[key] = {
            ...prevValue,
            raw: Object.keys(mergedRaw).length ? mergedRaw : prevValue.raw,
        };
    });
    return merged;
}


function applyProxyDefaults(variableList, baseline) {
    const out = {};
    const snapshotProxies = deriveProxyVariablesFromSnapshot(baseline?.market_snapshot);
    // Per-variable raw payloads from the backend (e.g. regulatory_friction
    // → { friction_intensity, state }). These power the "Based on:" line
    // for proxy variables that aren't derived from the market snapshot.
    const baselineRaws = baseline?.baseline?.raws || {};
    variableList.forEach(v => {
        const proxyRating = baseline?.baseline?.[v.key];
        const fromSnapshot = snapshotProxies[v.key];
        const fromRaws = baselineRaws[v.key];

        // Proxy-only variable (system-driven) — always seed rating + raw.
        // Snapshot-derived raw (vacancy, AMI) wins over backend raws because
        // the user can edit market figures, but we merge so backend-only
        // fields like regulatory_friction's `state` still surface.
        if (!v.userInput && proxyRating) {
            const seededRaw = { ...(fromRaws || {}), ...(fromSnapshot?.raw || {}) };
            out[v.key] = {
                rating: fromSnapshot?.rating || proxyRating,
                raw: Object.keys(seededRaw).length ? seededRaw : undefined,
                provenance: 'proxy',
            };
            return;
        }

        // User-input variable with a snapshot mapping (e.g. rent_to_ami_burden) —
        // seed the proxy raw (AMI, etc.) so it carries forward when the user
        // uploads a doc that only provides PARTIAL data (e.g. a rent roll
        // gives gross_rent but no AMI). Without this, the AMI is lost on
        // merge and the burden % can't be computed.
        if (v.userInput && fromSnapshot) {
            out[v.key] = { ...fromSnapshot, provenance: 'proxy' };
        }
    });
    return out;
}

// Re-derive proxy ratings AND raw figures from the market snapshot. We keep
// the raw alongside the rating so the variable row can show the actual
// numbers that drove the rating (vacancy %, AMI $, oversight strength) — not
// just an opaque category label.
function deriveProxyVariablesFromSnapshot(snapshot) {
    const out = {};
    if (!snapshot) return out;

    if (snapshot.vacancy_band || snapshot.rent_trend) {
        let rating = 'Moderate';
        if (snapshot.vacancy_band === 'High' || snapshot.rent_trend === 'Falling') rating = 'Weak';
        else if (snapshot.vacancy_band === 'Low' && snapshot.rent_trend === 'Rising') rating = 'Strong';
        out.market_stress = {
            rating,
            raw: { vacancy_band: snapshot.vacancy_band, rent_trend: snapshot.rent_trend },
        };
    }

    // Seed even if only PARTIAL data from the proxy — AMI alone is enough to
    // help downstream (rent from a doc combines with proxy AMI to compute %).
    if (snapshot.rent_to_AMI != null || snapshot.area_median_income != null) {
        const pct = snapshot.rent_to_AMI != null ? snapshot.rent_to_AMI * 100 : null;
        let rating = null;
        if (pct != null) {
            if (pct > 40) rating = 'Critical';
            else if (pct > 30) rating = 'Weak';
            else rating = 'Strong';
        }
        const proxyRaw = {};
        if (snapshot.rent_to_AMI != null)        proxyRaw.rent_to_AMI = snapshot.rent_to_AMI;
        if (pct != null)                          proxyRaw.rent_to_ami_pct = pct;
        if (snapshot.area_median_income != null)  proxyRaw.ami_for_unit_size = snapshot.area_median_income;
        if (snapshot.typical_rent != null)        proxyRaw.gross_rent = snapshot.typical_rent;
        out.rent_to_ami_burden = { rating, raw: proxyRaw };
    }

    if (snapshot.oversight_strength) {
        const rating =
            snapshot.oversight_strength === 'Strong'   ? 'Exceptional' :
            snapshot.oversight_strength === 'Moderate' ? 'Moderate' : 'Weak';
        out.oversight_credit = {
            rating,
            raw: { oversight_strength: snapshot.oversight_strength },
        };
    }

    return out;
}

export default function InputSelectionStep({ baseline, marketSnapshot, onChangeMarketSnapshot, refreshingBaseline = false, property, onChangeProperty, inputs, onChangeInputs, onRecordUpload, onBack, onNext }) {
    const [guidanceOpen, setGuidanceOpen] = useState(false);

    // Seed proxy defaults once on mount
    const [profInputs, setProfInputs] = useState(() => ({
        ...applyProxyDefaults(PROFITABILITY_VARIABLES, baseline),
        ...inputs.profitability,
    }));
    const [affInputs, setAffInputs] = useState(() => ({
        ...applyProxyDefaults(AFFORDABILITY_VARIABLES, baseline),
        ...inputs.affordability,
    }));

    // Re-sync proxy defaults when baseline changes. Two paths:
    //   - Variable not yet set in prev → use proxy default outright
    //   - Variable already set (e.g. from upload) → merge proxy raw fields IN
    //     to fill gaps. User's values win per-field; proxy fills only what's
    //     missing. So an uploaded rent roll's gross_rent stays, but proxy
    //     ami_for_unit_size fills in for the burden % calculation.
    useEffect(() => {
        if (!baseline) return;
        const profDefaults = applyProxyDefaults(PROFITABILITY_VARIABLES, baseline);
        const affDefaults  = applyProxyDefaults(AFFORDABILITY_VARIABLES, baseline);
        setProfInputs(prev => mergeProxyIntoState(profDefaults, prev));
        setAffInputs(prev  => mergeProxyIntoState(affDefaults,  prev));
    }, [baseline]);

    const update = (side, key, value) => {
        if (side === 'profitability') {
            const next = { ...profInputs, [key]: value };
            if (value == null) delete next[key];
            setProfInputs(next);
            onChangeInputs({ profitability: next, affordability: affInputs });
        } else {
            const next = { ...affInputs, [key]: value };
            if (value == null) delete next[key];
            setAffInputs(next);
            onChangeInputs({ profitability: profInputs, affordability: next });
        }
    };

    // Receive extracted variables from a doc upload in BuildingsEditor (e.g. an
    // NSPIRE that proves both PDD and Habitability) and route them into the
    // correct sub-score side. Each variable arrives as { rating?, raw?,
    // provenance: 'doc_verified', source_document } from the API.
    const PROF_KEYS = new Set(PROFITABILITY_VARIABLES.map(v => v.key));
    const AFF_KEYS  = new Set(AFFORDABILITY_VARIABLES.map(v => v.key));

    // Property metadata pulled from a doc upload — auto-fills the property fields.
    // Existing values stay put unless the doc actually returned something for them
    // (so a user-edited address won't be clobbered by an empty extraction field).
    //
    // CRITICAL: must use the functional setter form. Buildings and metadata both
    // update during the same upload callback; if we read `property` from closure
    // here, we'd overwrite the buildings update that fires alongside.
    const ingestExtractedProperty = (extracted) => {
        onChangeProperty(prev => {
            const merged = { ...prev };
            ['property_id', 'address', 'zip_code', 'placed_in_service'].forEach(k => {
                if (extracted[k] && !merged[k]) merged[k] = extracted[k];
            });
            if (extracted.units && !merged.units) merged.units = extracted.units;
            if (extracted.buildings_count && !merged.buildings_count) merged.buildings_count = extracted.buildings_count;
            if (extracted.overall_condition && !merged.overall_condition) merged.overall_condition = extracted.overall_condition;
            if (Array.isArray(extracted.programs) && extracted.programs.length) {
                const existing = new Set(merged.programs || []);
                extracted.programs.forEach(p => existing.add(p));
                merged.programs = Array.from(existing);
            }
            return merged;
        });
    };

    const ingestExtractedVariables = (extracted, sourceFilename) => {
        const profPatch = {};
        const affPatch = {};

        Object.entries(extracted).forEach(([key, payload]) => {
            const isProf = PROF_KEYS.has(key);
            const isAff = AFF_KEYS.has(key);
            if (!isProf && !isAff) return;

            // Merge raw with whatever's already there (proxy fields, prior uploads).
            // CRITICAL: filter out null/undefined from the incoming raw before
            // spreading, so explicit nulls from Gemini ("I didn't see AMI in
            // this doc") don't clobber proxy values that DO have data.
            const existing = isProf ? profInputs[key] : affInputs[key];
            const incomingRaw = Object.fromEntries(
                Object.entries(payload?.raw || {}).filter(([, v]) => v != null && v !== '')
            );
            const mergedRaw = {
                ...(existing?.raw || {}),
                ...incomingRaw,
            };

            const value = {
                rating: payload?.rating || null,
                raw: Object.keys(mergedRaw).length ? mergedRaw : null,
                provenance: payload?.provenance || 'doc_verified',
                source_document: payload?.source_document || sourceFilename,
            };
            if (isProf) profPatch[key] = value;
            else        affPatch[key]  = value;
        });

        const nextProf = { ...profInputs, ...profPatch };
        const nextAff  = { ...affInputs,  ...affPatch  };
        setProfInputs(nextProf);
        setAffInputs(nextAff);
        onChangeInputs({ profitability: nextProf, affordability: nextAff });
    };

    const baselineScores = baseline?.baseline_score;

    // Stale-score signal: the baseline ScoreCard was computed from proxies only.
    // The moment the user uploads a doc, picks a self-selected rating, or edits
    // a market figure, that number no longer reflects the current input state.
    // Surfaces an asterisk + caption nudging them toward Calculate.
    const baselineStale = useMemo(() => {
        const all = { ...profInputs, ...affInputs };
        return Object.values(all).some(v => v?.provenance && v.provenance !== 'proxy');
    }, [profInputs, affInputs]);

    // When the user corrects market figures, re-derive the affected proxy ratings
    // (and their underlying raw figures) and route them into the affordability
    // inputs. Variable rows refresh because the parent inputs state changes.
    const handleSnapshotChange = (next) => {
        onChangeMarketSnapshot?.(next);
        const derived = deriveProxyVariablesFromSnapshot(next);
        const affPatch = {};
        Object.entries(derived).forEach(([key, value]) => {
            if (key === 'market_stress' || key === 'rent_to_ami_burden' || key === 'oversight_credit') {
                affPatch[key] = { ...value, provenance: 'proxy' };
            }
        });
        if (Object.keys(affPatch).length) {
            const nextAff = { ...affInputs, ...affPatch };
            setAffInputs(nextAff);
            onChangeInputs({ profitability: profInputs, affordability: nextAff });
        }
    };

    return (
        <div>
            <div className="mb-6 flex items-start justify-between gap-4">
                <div>
                    <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-1">Supply each variable</h2>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                        Upload a High-Trust document (unlocks 90-100) or self-select a rating (caps at 89). Variables marked
                        system-driven are filled from public proxies — the figure updates live as you answer.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={() => setGuidanceOpen(true)}
                    className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 border border-blue-200 dark:border-blue-800 transition-colors"
                    title="Document framework — what to upload, what to skip"
                >
                    <BookOpen size={14} />
                    Document guidance
                </button>
            </div>

            <DocumentGuidanceModal isOpen={guidanceOpen} onClose={() => setGuidanceOpen(false)} />

            {refreshingBaseline && (
                <div className="mb-6">
                    <div className="mb-2 text-sm text-slate-700 dark:text-slate-200">
                        Refreshing baseline for <span className="font-medium">{property.address || property.zip_code || 'this property'}</span>…
                    </div>
                    <BaselineFetchProgress active />
                </div>
            )}

            {!refreshingBaseline && baselineScores && (
                <div className="mb-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
                    <div className="lg:col-span-1">
                        <ScoreCard scores={baselineScores} tier={baseline.tier} confidence="Proxy" cappedByConfidenceGate stale={baselineStale} />
                    </div>
                    {marketSnapshot && (
                        <div className="lg:col-span-2">
                            <BaselineSummary snapshot={marketSnapshot} onChange={handleSnapshotChange} />
                        </div>
                    )}
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <label className="block">
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-200">Property ID / Name</span>
                    <input value={property.property_id || ''} onChange={(e) => onChangeProperty({ ...property, property_id: e.target.value })}
                           className="mt-1 w-full px-3 py-2 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-blue-500" />
                </label>

                <label className="block md:col-span-2">
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-200">
                        Address <span className="text-xs text-slate-500 font-normal">— picking one updates ZIP and refreshes proxies</span>
                    </span>
                    <div className="mt-1">
                        <AddressAutocomplete
                            value={property.address || ''}
                            onChange={(val) => onChangeProperty({ ...property, address: val })}
                            onPlaceSelect={(p) => onChangeProperty({
                                ...property,
                                address: p.address,
                                zip_code: p.zip_code || property.zip_code,
                            })}
                        />
                    </div>
                </label>

                <label className="block">
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-200">Units</span>
                    <input type="number" min={1} value={property.units || ''} onChange={(e) => onChangeProperty({ ...property, units: parseInt(e.target.value, 10) || undefined })}
                           className="mt-1 w-full px-3 py-2 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-blue-500" />
                </label>

                <label className="block">
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-200">ZIP</span>
                    <input value={property.zip_code || ''} onChange={(e) => onChangeProperty({ ...property, zip_code: e.target.value.replace(/\D/g, '').slice(0, 5) })}
                           className="mt-1 w-full px-3 py-2 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-blue-500" />
                </label>

                <label className="block">
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-200">
                        Placed in Service <span className="text-xs text-slate-500 font-normal">— year built / PIS</span>
                    </span>
                    <input type="number" min={1900} max={new Date().getFullYear()} placeholder="e.g. 2005"
                           value={(property.placed_in_service || '').toString().slice(0, 4)}
                           onChange={(e) => onChangeProperty({ ...property, placed_in_service: e.target.value || undefined })}
                           className="mt-1 w-full px-3 py-2 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-blue-500" />
                </label>

                <div className="md:col-span-3">
                    <span className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-2">Programs</span>
                    <ProgramChips value={property.programs || []}
                                  onChange={(next) => onChangeProperty({ ...property, programs: next })} />
                </div>
            </div>

            <BuildingsEditor
                buildings={property.buildings || []}
                onChange={(next) => onChangeProperty(prev => ({ ...prev, buildings: next }))}
                onExtractedVariables={ingestExtractedVariables}
                onExtractedProperty={ingestExtractedProperty}
                onRecordUpload={onRecordUpload}
            />

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <section>
                    <h3 className="text-sm font-semibold uppercase tracking-wider text-blue-600 dark:text-blue-400 mb-3">Profitability (7)</h3>
                    <div className="space-y-3">
                        {PROFITABILITY_VARIABLES.map(v => (
                            <VariableRow key={v.key} variable={v} value={profInputs[v.key]}
                                         onChange={(val) => update('profitability', v.key, val)}
                                         onRecordUpload={onRecordUpload}
                                         onIngestVariables={ingestExtractedVariables}
                                         onExtractedProperty={ingestExtractedProperty}
                                         onExtractedBuildings={(bldgs) => onChangeProperty(prev => ({
                                             ...prev,
                                             buildings: [...(prev.buildings || []), ...bldgs.filter(b => !(prev.buildings || []).some(eb => eb.name === b.name))]
                                         }))} />
                        ))}
                    </div>
                </section>
                <section>
                    <h3 className="text-sm font-semibold uppercase tracking-wider text-green-600 dark:text-green-400 mb-3">Affordability (6)</h3>
                    <div className="space-y-3">
                        {AFFORDABILITY_VARIABLES.map(v => (
                            <VariableRow key={v.key} variable={v} value={affInputs[v.key]}
                                         onChange={(val) => update('affordability', v.key, val)}
                                         onRecordUpload={onRecordUpload}
                                         onIngestVariables={ingestExtractedVariables}
                                         onExtractedProperty={ingestExtractedProperty}
                                         onExtractedBuildings={(bldgs) => onChangeProperty(prev => ({
                                             ...prev,
                                             buildings: [...(prev.buildings || []), ...bldgs.filter(b => !(prev.buildings || []).some(eb => eb.name === b.name))]
                                         }))} />
                        ))}
                    </div>
                </section>
            </div>

            <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-200 dark:border-slate-700">
                <button onClick={onBack} className="inline-flex items-center gap-2 text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white">
                    <ArrowLeft size={16} /> Back
                </button>
                <button onClick={onNext} disabled={!property.units}
                        className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium px-5 py-2.5 rounded-md disabled:opacity-50">
                    Calculate Score <ArrowRight size={16} />
                </button>
            </div>
        </div>
    );
}
